import React, { useCallback } from 'react';
import { StatusBar } from 'react-native';
import { CommonActions, useNavigation, useNavigationState } from '@react-navigation/native';
import { ResidentMobileHeaderWithMenu } from './ResidentMobileHeaderWithMenu';
import { ResidentMobileNavigator } from '@/navigation/residents/mobile/ResidentMobileNavigator';
import { GradientBackground } from '@/components/residents/redesign/GradientBackground';
import { getResidentScreenMetadata } from '@/hooks/residents/useResidentScreen';
import { RESIDENT_PATH_TO_SCREEN } from '@/constants/residents/residentNavigation';
import { useAppSelector, useAppDispatch } from '@/store';
import { logout } from '@/store/slices/authSlice';

export const ResidentMobileLayout: React.FC = () => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  // Read current route from inner navigator
  const currentRouteName = useNavigationState((state) => {
    const mainRoute = state?.routes?.find((r: any) => r.name === 'Main');
    const innerState = mainRoute?.state;
    if (innerState) {
      return innerState.routes[innerState.index ?? 0]?.name ?? 'ResidentHome';
    }
    return 'ResidentHome';
  });

  // Derive header variant — Home with property image uses 'image', all else 'gradient'
  const propertyImageUrl: string | null = user?.property?.photoUrl || null;
  const headerVariant: 'gradient' | 'image' = currentRouteName === 'ResidentHome' && propertyImageUrl
    ? 'image'
    : 'gradient';

  // Screen metadata for nav highlighting and title
  const screenMetadata = getResidentScreenMetadata(currentRouteName);

  // Navigation handler — reset root stack to Main with target inner screen
  const handleNavigate = useCallback((path: string) => {
    const screenName = RESIDENT_PATH_TO_SCREEN[path];
    if (!screenName) {
      console.warn(`No screen mapping found for path: ${path}`);
      return;
    }

    if (screenName === 'WifiConnection') {
      navigation.navigate('WifiConnection' as any);
      return;
    }

    navigation.dispatch((state: any) => {
      const mainRoute = state?.routes?.find((r: any) => r.name === 'Main');
      return CommonActions.reset({
        index: 0,
        routes: [{
          key: mainRoute?.key,
          name: 'Main',
          state: {
            routes: [{ name: screenName }],
            index: 0,
          },
        }],
      });
    });
  }, [navigation]);

  // Logo press — reset to home
  const handleLogoPress = useCallback(() => {
    navigation.dispatch((state: any) => {
      const mainRoute = state?.routes?.find((r: any) => r.name === 'Main');
      return CommonActions.reset({
        index: 0,
        routes: [{
          key: mainRoute?.key,
          name: 'Main',
          state: {
            routes: [{ name: 'ResidentHome' }],
            index: 0,
          },
        }],
      });
    });
  }, [navigation]);

  // Sign out
  const handleSignOut = useCallback(async () => {
    try {
      await dispatch(logout()).unwrap();
    } catch (error: any) {
      console.error('Resident sign out failed:', error);
    }
  }, [dispatch]);

  // Notification press — reset to notifications screen
  const handleNotificationPress = useCallback(() => {
    navigation.dispatch((state: any) => {
      const mainRoute = state?.routes?.find((r: any) => r.name === 'Main');
      return CommonActions.reset({
        index: 0,
        routes: [{
          key: mainRoute?.key,
          name: 'Main',
          state: {
            routes: [{ name: 'ResidentNotifications' }],
            index: 0,
          },
        }],
      });
    });
  }, [navigation]);

  return (
    <GradientBackground blendEdges propertyImageUrl={currentRouteName === 'ResidentHome' ? propertyImageUrl : undefined}>
      <StatusBar barStyle="light-content" />
      <ResidentMobileHeaderWithMenu
        onLogoPress={handleLogoPress}
        onSignOut={handleSignOut}
        onNavigate={handleNavigate}
        onNotificationPress={handleNotificationPress}
        currentPath={screenMetadata.path}
        currentTitle={screenMetadata.title}
        showNotification={true}
        variant={headerVariant}
      />
      <ResidentMobileNavigator />
    </GradientBackground>
  );
};
